export const DEFAULT_COVER_LETTER_SECTIONS = [
  "date",
  "recipient",
  "reference",
  "letter",
  "signoff",
  "enclosures",
] as const;

export const defaultCoverLetterLayout = [[[...DEFAULT_COVER_LETTER_SECTIONS], []]];

const insertAt = (sections: string[], key: string, index: number) => {
  if (sections.includes(key)) return sections;
  return [...sections.slice(0, index), key, ...sections.slice(index)];
};

/** Place a section before Sign-off, else before Enclosures, else at the end. */
export const insertBeforeClosing = (sections: string[], key: string): string[] => {
  if (sections.includes(key)) return sections;
  const signoffAt = sections.indexOf("signoff");
  if (signoffAt !== -1) return insertAt(sections, key, signoffAt);
  const enclosuresAt = sections.indexOf("enclosures");
  if (enclosuresAt !== -1) return insertAt(sections, key, enclosuresAt);
  return [...sections, key];
};

export const insertBeforeClosingInPlace = (column: string[], key: string) => {
  const next = insertBeforeClosing(column, key);
  if (next === column) return;
  column.length = 0;
  column.push(...next);
};

/** Legacy layouts omitted `signoff` because it was baked into Letter Content. */
export const ensureSignoffInSections = (sections: string[]): string[] => {
  if (sections.includes("signoff")) return sections;
  const enclosuresAt = sections.indexOf("enclosures");
  if (enclosuresAt !== -1) return insertAt(sections, "signoff", enclosuresAt);
  const letterAt = sections.indexOf("letter");
  if (letterAt !== -1) return insertAt(sections, "signoff", letterAt + 1);
  return [...sections, "signoff"];
};

const dedupe = (sections: string[]) => {
  const seen = new Set<string>();
  return sections.filter((key) => {
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const appendMissingCustomKeys = (
  sections: string[],
  custom: Record<string, unknown> | undefined,
  alreadyListed: Set<string>,
) => {
  let next = sections;
  for (const id of Object.keys(custom ?? {})) {
    const key = `custom.${id}`;
    if (alreadyListed.has(key)) continue;
    next = insertBeforeClosing(next, key);
    alreadyListed.add(key);
  }
  return next.filter((key) => {
    if (!key.startsWith("custom.")) return true;
    const customId = key.slice("custom.".length);
    return Boolean(custom?.[customId]);
  });
};

export const flattenCoverLetterLayoutItems = (
  layout: string[][][] | undefined,
  custom: Record<string, unknown> | undefined,
): string[] => {
  const sections: string[] = [];

  if (layout && layout.length > 0 && layout[0]) {
    for (const page of layout) {
      if (page[0]) sections.push(...page[0]);
      if (page[1] && page[1].length > 0) sections.push(...page[1]);
    }
  } else {
    sections.push(...DEFAULT_COVER_LETTER_SECTIONS);
  }

  const unique = ensureSignoffInSections(dedupe(sections));
  return appendMissingCustomKeys(unique, custom, new Set(unique));
};

export const resolveCoverLetterLayout = (
  layout: string[][][] | undefined,
  custom: Record<string, unknown> | undefined,
) => {
  let mainSections: string[] = [];
  const sidebarSections: string[] = [];

  if (layout && layout.length > 0 && layout[0]) {
    mainSections.push(...(layout[0][0] || []));
    sidebarSections.push(...(layout[0][1] || []));
  } else {
    mainSections.push(...DEFAULT_COVER_LETTER_SECTIONS);
  }

  mainSections = dedupe(mainSections);
  const allLayoutKeys = new Set([...mainSections, ...sidebarSections]);

  if (!allLayoutKeys.has("signoff")) {
    mainSections = ensureSignoffInSections(mainSections);
    allLayoutKeys.add("signoff");
  }

  mainSections = appendMissingCustomKeys(mainSections, custom, allLayoutKeys);

  return {
    mainSections,
    sidebarSections,
  };
};
